import { DialogComponent } from '@theme/dialog';
import { onAnimationEnd } from '@theme/utilities';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

class HeaderMenuDialog extends DialogComponent {
  requiredRefs = ['dialog', 'trigger', 'rootPanel'];

  constructor() {
    super();
    const baseCloseDialog = this.closeDialog;
    /** @param {{ immediate?: boolean }} [options] */
    this.closeDialog = async (options = {}) => {
      if (!options.immediate && this.refs.dialog instanceof HTMLDialogElement && this.refs.dialog.open) {
        const openPads = this.querySelector('.header-menu-item__pads.is-menu-open');
        if (openPads instanceof HTMLElement) {
          const padElements = [...openPads.querySelectorAll('.header-menu-pad')].filter(
            (el) => el instanceof HTMLElement
          );
          openPads.classList.remove('is-menu-open');
          if (padElements.length > 0) {
            await onAnimationEnd(padElements, undefined, { subtree: false });
          }
        }
      }
      return baseCloseDialog(options);
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    this.addEventListener('dialog:open', this.#handleDialogOpen);
    this.addEventListener('dialog:close', this.#handleDialogClose);
    this.addEventListener('shopify:block:select', this.#handleBlockSelect);
    this.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('dialog:open', this.#handleDialogOpen);
    this.removeEventListener('dialog:close', this.#handleDialogClose);
    this.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
  }

  /** @type {string | null} */
  #activeMenuId = null;

  /** @param {CustomEvent} _event */
  #handleDialogOpen = (_event) => {
    this.#trigger?.setAttribute('aria-expanded', 'true');
    this.#setOpenPads(null);

    requestAnimationFrame(() => {
      this.#focusFirst(this.#rootPanel);
    });
  };

  /** @param {CustomEvent} _event */
  #handleDialogClose = (_event) => {
    this.#closeActiveMenu({ restoreFocus: false });
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#trigger?.focus();
  };

  /** @param {CustomEvent} event */
  #handleBlockSelect = (event) => {
    const item = /** @type {HTMLElement} */ (event.target)?.closest('[data-menu-item]');
    if (!item) return;

    this.showDialog({ immediate: true });

    const trigger = item.querySelector('[data-menu-open]');
    if (trigger instanceof HTMLButtonElement) {
      this.#openMenu(trigger);
    }
  };

  /** @param {CustomEvent} _event */
  #handleBlockDeselect = (_event) => {
    this.#closeActiveMenu({ restoreFocus: false });
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const openButton = event.target.closest('[data-menu-open]');
    if (openButton instanceof HTMLButtonElement) {
      this.#openMenu(openButton);
      return;
    }

    const backButton = event.target.closest('[data-menu-back]');
    if (backButton instanceof HTMLButtonElement) {
      this.#closeActiveMenu();
    }
  };

  /**
   * Intercepts Escape when a submenu is active to close it instead of the dialog.
   * Relies on this handler being registered in connectedCallback() (before
   * DialogComponent registers its own in showDialog()) so that
   * stopImmediatePropagation() prevents the parent from also closing the dialog.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#activeMenu) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#closeActiveMenu();
    }
  };

  /** @param {HTMLButtonElement} trigger */
  #openMenu(trigger) {
    const menuId = trigger.getAttribute('aria-controls');
    if (!menuId) return;

    if (this.#activeMenuId === menuId) {
      return;
    }

    this.#closeActiveMenu({ restoreFocus: false });

    const panel = this.querySelector(`#${CSS.escape(menuId)}`);
    const item = trigger.closest('[data-menu-item]');

    if (!(panel instanceof HTMLElement) || !(item instanceof HTMLElement)) {
      return;
    }

    this.#activeMenuId = menuId;
    item.dataset.menuActive = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.#setMenuIsolation(item);
    this.#setOpenPads(trigger.id);

    requestAnimationFrame(() => {
      this.#focusFirst(panel);
    });
  }

  #closeActiveMenu({ restoreFocus = true } = {}) {
    const activeMenu = this.#activeMenu;
    if (!activeMenu) return;

    const { item, trigger, panel } = activeMenu;

    delete item.dataset.menuActive;
    trigger.setAttribute('aria-expanded', 'false');
    panel.hidden = true;
    this.#activeMenuId = null;
    this.#clearMenuIsolation();
    this.#setOpenPads(null);

    if (restoreFocus) {
      trigger.focus();
    }
  }

  /** @param {string | null} triggerId */
  #setOpenPads(triggerId) {
    for (const pads of this.#pads) {
      const isOpen = Boolean(triggerId) && pads.getAttribute('data-menu-pads-for') === triggerId;
      pads.classList.toggle('is-menu-open', isOpen);
    }
  }

  /** @param {HTMLElement} activeItem */
  #setMenuIsolation(activeItem) {
    for (const item of this.#menuItems) {
      item.inert = item !== activeItem;
    }
  }

  #clearMenuIsolation() {
    for (const item of this.#menuItems) {
      item.inert = false;
    }
  }

  /** @param {HTMLElement | null} container */
  #focusFirst(container) {
    if (!(container instanceof HTMLElement)) return;

    const target = container.querySelector(FOCUSABLE_SELECTOR);
    if (target instanceof HTMLElement) {
      target.focus();
      return;
    }

    container.focus();
  }

  get #rootPanel() {
    return this.refs.rootPanel instanceof HTMLElement ? this.refs.rootPanel : null;
  }

  get #trigger() {
    return this.refs.trigger instanceof HTMLElement ? this.refs.trigger : null;
  }

  get #menuItems() {
    return [...this.querySelectorAll('[data-menu-item]')].filter((item) => item instanceof HTMLElement);
  }

  get #pads() {
    return [...this.querySelectorAll('.header-menu-item__pads')].filter((item) => item instanceof HTMLElement);
  }

  get #activeMenu() {
    if (!this.#activeMenuId) return null;

    const panel = this.querySelector(`#${CSS.escape(this.#activeMenuId)}`);
    if (!(panel instanceof HTMLElement)) return null;

    const item = panel.closest('[data-menu-item]');
    const trigger = item?.querySelector('[data-menu-open]');

    if (!(item instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) {
      return null;
    }

    return { item, trigger, panel };
  }
}

if (!customElements.get('header-menu-dialog')) {
  customElements.define('header-menu-dialog', HeaderMenuDialog);
}